import { useEffect, useRef, useState } from "react";
import {
  Camera,
  X,
  ImagePlus,
  FlipHorizontal2,
  Music2,
  Circle,
  Square,
  Video,
  LoaderCircle,
} from "lucide-react";
import { IconButton } from "./components";
export default function CameraPage({
  onClose,
  onFile,
  notify,
  title = "记录此刻的精彩",
}: {
  onClose: () => void;
  onFile: (file: File) => void;
  notify: (text: string) => void;
  title?: string;
}) {
  const preview = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [limit, setLimit] = useState(60);
  const [facing, setFacing] = useState<"user" | "environment">("environment");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (recorder.current && recorder.current.state !== "inactive") {
        recorder.current.onstop = null;
        recorder.current.stop();
      }
      stream.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);
  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [recording]);
  useEffect(() => {
    if (recording && seconds >= limit) recorder.current?.stop();
  }, [seconds, recording, limit]);
  useEffect(() => {
    const stopForBackground = () => {
      if (recorder.current?.state === "recording") recorder.current.stop();
      stream.current?.getTracks().forEach((t) => t.stop());
      if (preview.current) preview.current.srcObject = null;
      setReady(false);
    };
    const pause = () => {
      if (document.hidden) stopForBackground();
    };
    const native = (e: Event) => {
      if (!(e as CustomEvent<boolean>).detail) stopForBackground();
    };
    document.addEventListener("visibilitychange", pause);
    window.addEventListener("native-active", native);
    return () => {
      document.removeEventListener("visibilitychange", pause);
      window.removeEventListener("native-active", native);
    };
  }, []);
  async function enableCamera(direction = facing) {
    if (!navigator.mediaDevices?.getUserMedia) {
      notify("当前环境不支持相机预览，请使用相册或系统拍摄");
      fileInput.current?.click();
      return;
    }
    setBusy(true);
    setReady(false);
    stream.current?.getTracks().forEach((t) => t.stop());
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: direction },
          width: { ideal: 1080 },
          height: { ideal: 1920 },
        },
        audio: true,
      });
      if (!mounted.current) {
        media.getTracks().forEach((t) => t.stop());
        return;
      }
      stream.current = media;
      if (preview.current) {
        preview.current.srcObject = media;
        await preview.current.play();
      }
      setReady(true);
    } catch {
      notify("未能开启相机，请检查权限，或从相册选择视频");
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  function record() {
    if (recording) {
      recorder.current?.stop();
      return;
    }
    if (!ready) {
      void enableCamera();
      return;
    }
    if (!stream.current || typeof MediaRecorder === "undefined") {
      notify("当前设备不支持网页录制，请使用系统拍摄");
      fileInput.current?.click();
      return;
    }
    try {
      const mime = [
        "video/mp4",
        "video/webm;codecs=vp9,opus",
        "video/webm",
      ].find((m) => MediaRecorder.isTypeSupported(m));
      const rec = new MediaRecorder(
        stream.current,
        mime ? { mimeType: mime } : undefined,
      );
      chunks.current = [];
      recorder.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      rec.onstop = () => {
        setRecording(false);
        const blob = new Blob(chunks.current, { type: rec.mimeType });
        if (mounted.current && blob.size)
          onFile(
            new File(
              [blob],
              `frame-${Date.now()}.${rec.mimeType.includes("mp4") ? "mp4" : "webm"}`,
              { type: rec.mimeType },
            ),
          );
      };
      rec.start(1000);
      setSeconds(0);
      setRecording(true);
    } catch {
      notify("录制不可用，请选择相册或系统拍摄");
    }
  }
  return (
    <section className="camera-page">
      <video className="camera-preview" ref={preview} playsInline muted />
      {!ready && (
        <div className="camera-placeholder">
          <Camera size={50} strokeWidth={1} />
          <h2>{title}</h2>
          <p>开启相机，或选择一段你喜欢的视频</p>
          <button
            className="primary"
            disabled={busy}
            onClick={() => void enableCamera()}
          >
            {busy ? "正在开启…" : "开启相机"}
          </button>
          <small>仅在你点击后申请相机和麦克风权限</small>
        </div>
      )}
      <header className="camera-header">
        <IconButton label="关闭拍摄" onClick={onClose}>
          <X />
        </IconButton>
        <span className="camera-title">
          <Music2 size={16} /> 原声拍摄
        </span>
        <IconButton
          label="切换摄像头"
          onClick={() => {
            if (recording || busy) return;
            const next = facing === "user" ? "environment" : "user";
            setFacing(next);
            if (ready) void enableCamera(next);
          }}
        >
          <FlipHorizontal2 />
        </IconButton>
      </header>
      <div className="camera-bottom">
        {recording && (
          <p className="recording-label">
            ● {seconds}s / {limit}s
          </p>
        )}
        <p>视频</p>
        <div className="capture-row">
          <button
            className="camera-side"
            onClick={() => fileInput.current?.click()}
            disabled={recording}
          >
            <ImagePlus />
            <span>相册</span>
          </button>
          <button
            className={`shutter ${recording ? "recording" : ""}`}
            disabled={busy}
            aria-label={
              recording ? "停止录制" : ready ? "开始录制" : "开启相机"
            }
            onClick={record}
          >
            {busy ? (
              <LoaderCircle className="spin" />
            ) : recording ? (
              <Square fill="currentColor" />
            ) : (
              <Circle fill="currentColor" />
            )}
          </button>
          <label className="camera-side">
            <Video />
            <span>系统拍摄</span>
            <input
              type="file"
              accept="video/*"
              capture="environment"
              disabled={recording}
              onChange={(e) => {
                if (e.target.files?.[0]) onFile(e.target.files[0]);
              }}
            />
          </label>
        </div>
        <div className="duration-tabs">
          {[15, 60, 180].map((n) => (
            <button
              key={n}
              disabled={recording}
              className={n === limit ? "selected" : ""}
              onClick={() => setLimit(n)}
            >
              {n === 180 ? "3分钟" : `${n}秒`}
            </button>
          ))}
        </div>
      </div>
      <input
        className="hidden-input"
        ref={fileInput}
        type="file"
        accept="video/*"
        onChange={(e) => {
          if (e.target.files?.[0]) onFile(e.target.files[0]);
          e.target.value = "";
        }}
      />
    </section>
  );
}
